<template lang="pug">
.receive-request-histories-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    //- ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    //- レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    //- フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    //- セル：状態
    template(#cell(status)="row")
      span(:class="getStatusCssClass(row.item)") {{ convertStatus(row.item) }}
    //- セル：受信日時
    template(#cell(datetime)="row")
      span {{ convertSendDatetime(row.item) }}
    //- セル：送信事業所
    template(#cell(facilityName)="row")
      span {{ row.item.facilityName ? row.item.facilityName : '' }}
    //- セル：返信状態（最新）
    template(#cell(acceptance)="row")
      span(v-if="row.item.latestAcceptance" :class="getAcceptanceCssClass(row.item.latestAcceptance)") {{ convertLatestAcceptance(row.item.latestAcceptance) }}
    //- セル：操作
    template(#cell(control)="row")
      button.btn.detail-btn.mr-2(@click="onRequestDetail(row.item)") 受信内容
      button.btn.reply-btn(:class="[{ disable: disableReplyButton(row.item) }]" @click="onReply(row.item)" :disabled="disableReplyButton(row.item)") 返信
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import { OtherOfficeAcceptanceRequestsGetModel } from "@/commons/services/OtherOfficeAcceptanceRequests";

import StatusSwitch from "@/pc/basics/StatusSwitch.vue";
import { Acceptance } from "@/commons/services/OtherOfficeAcceptanceRequestReplies";

@Component({
  components: {
    StatusSwitch
  }
})
export default class ReceiveRequestHistoriesTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<ReceiveRequestHistoriesTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<ReceiveRequestHistoriesTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: FilterSetting;

  get _value(): Array<ReceiveRequestHistoriesTableItem> {
    return this.value;
  }

  /**
   * ### フィルタリング
   */
  filterTable(row: ReceiveRequestHistoriesTableItem, filter: FilterSetting): boolean {
    return this.filterSearchWords(row, filter);
  }

  private filterSearchWords(row: ReceiveRequestHistoriesTableItem, filter: FilterSetting): boolean {
    if (filter.searchWord == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.searchWord);

    let foundFacilityName: boolean = true;

    for (const word of words) {
      // 宛先
      if (row.facilityName && row.facilityName.indexOf(word) == -1) {
        foundFacilityName = false;
      }
    }

    return foundFacilityName;
  }

  /**
   * ### フィルタリングイベント
   */
  onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### 状態に対するCSSクラスを返す
   */
  getStatusCssClass(item: ReceiveRequestHistoriesTableItem): string {
    if (item.ended == true) {
      return "ended";
    }
    if (item.replied == false) {
      return "no-reply";
    } else {
      return "replied";
    }
  }

  /**
   * ### 状態を返す
   */
  convertStatus(item: ReceiveRequestHistoriesTableItem): string {
    if (item.ended == true) {
      return "終了";
    }
    if (item.replied == false) {
      return "未返信";
    } else {
      return "返信済";
    }
  }

  /**
   * ### 画面表示用の日付を返す
   */
  convertSendDatetime(item: ReceiveRequestHistoriesTableItem): string {
    if (item.datetime) {
      return this.$dateUtil.convertNumberToString(Number(item.datetime), "yyyy/mm/dd hh:MM");
    } else {
      return "";
    }
  }

  /**
   * ### 返信内容の受け入れ可否のCSSクラスを返す
   */
  getAcceptanceCssClass(acceptance: Acceptance): string {
    switch (acceptance) {
      case Acceptance.POSSIBLE: {
        return "possible";
      }
      case Acceptance.NEED_CONSULTATION: {
        return "need-consultation";
      }
      case Acceptance.NOT_POSSIBLE: {
        return "not-possible";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### 返信内容の受け入れ可否の文字列を返す
   */
  convertLatestAcceptance(acceptance: Acceptance): string {
    switch (acceptance) {
      case Acceptance.POSSIBLE: {
        return "受け入れ可";
      }
      case Acceptance.NEED_CONSULTATION: {
        return "詳細相談希望";
      }
      case Acceptance.NOT_POSSIBLE: {
        return "受け入れ不可";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### 返信ボタンの非活性可否
   */
  disableReplyButton(item: ReceiveRequestHistoriesTableItem): boolean {
    // 状態が終了の場合は返信不可なので非活性
    if (item.ended == true) {
      return true;
    }
    return false;
  }

  /**
   * ### 詳細ボタン押下イベント
   */
  onRequestDetail(item: ReceiveRequestHistoriesTableItem): void {
    this.$emit("onRequestDetail", item);
    return;
  }

  /**
   * ### 返信ボタン押下イベント
   */
  onReply(item: ReceiveRequestHistoriesTableItem): void {
    this.$emit("onReply", item);
    return;
  }
}

/** テーブルの列名の情報 */
export interface ReceiveRequestHistoriesTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface ReceiveRequestHistoriesTableItem extends OtherOfficeAcceptanceRequestsGetModel {}

export interface FilterSetting {
  /** 検索ワード */
  searchWord: string;
}
</script>

<style lang="scss" scoped>
.receive-request-histories-table-component {
  .detail-btn {
    width: 104px;
  }
  .reply-btn {
    width: 64px;
  }
}
</style>
